/**
 * How a finished coding turn is laid out: one collapsed summary, then the answer.
 *
 * A coding turn is dozens of steps ending in a paragraph that says what changed.
 * While it runs, those steps are the interesting part and `buildActivitySegments`
 * plus `CodingToolRun` already show the newest one live. Once it ends they are
 * history, and leaving them stacked down the transcript buries the one thing the
 * user came back for. So a completed turn folds every step behind a single
 * `14 steps · 3m 12s ✓` row and leaves the closing text at full prominence.
 *
 * The fold is *whole-turn*, not per-run: the narration a model writes between
 * tool calls ("Now let me add the tests") is machine chatter that happens to be
 * prose, and folding the steps but not the chatter would leave the transcript
 * exactly as long as before. Only the text the turn ends on is an answer.
 *
 * Two things never fold, whatever they sit next to:
 *   - content tools (`ask`, `render_ui`, …) — a question with its controls
 *     hidden behind a chevron is a dead end;
 *   - the plan card, which is the one row that still says something after the
 *     turn is over.
 * Either one closes the group it interrupts and opens a new one, so a turn that
 * stops to ask something reads as two phases rather than one.
 *
 * Pure and DOM-free, so the layout can be tested from part fixtures.
 *
 * @module components/chat/messages/coding-turn
 */

import type { ChatMessage } from "@/types/chat-message";
import {
  isActivityPart,
  isTextType,
  partSignature,
  rendersNothing,
  type ActivityItem,
} from "./activity-segments";

type MessagePart = ChatMessage["parts"][number];

/**
 * One piece of a coding turn's layout.
 *
 * `part` and `run` are the live timeline: standalone parts, and runs of
 * consecutive steps that `CodingToolRun` draws newest-first. `summary` wraps a
 * stretch of that same timeline behind one row once the turn has ended — note
 * it carries *segments*, not raw steps, so expanding it restores the timeline
 * verbatim rather than re-deriving a flattened version of it.
 */
export type CodingTimelineSegment =
  | { kind: "part"; index: number }
  | { kind: "run"; id: string; items: ActivityItem[]; stepCount: number };

export type CodingTurnSegment =
  | CodingTimelineSegment
  | { kind: "summary"; id: string; stepCount: number; segments: CodingTimelineSegment[] };

export interface CodingTurnOptions {
  /** True while this message is still being produced. */
  isMessageStreaming: boolean;
  /** False when the workspace hides tool calls — then there are no steps to fold. */
  showToolActivity: boolean;
}

/**
 * Whether a part joins a run rather than standing on its own.
 *
 * Exactly the assistant surface's rule, with nothing added. An earlier version
 * held the plan card out of runs so it would survive the fold; that put the card
 * somewhere it had never been while the turn streamed, which is the one thing
 * this layout must not do. A plan is a step like any other.
 */
function joinsRun(part: MessagePart, showToolActivity: boolean): boolean {
  return isActivityPart(part, showToolActivity);
}

/**
 * Where the answer begins: the index after the last step of the turn.
 *
 * Walks back over the trailing text and stops at the last thing that was work.
 * A turn that ends on a tool call — aborted, or one that simply had nothing to
 * say — has no answer, and every part belongs to the fold.
 */
function answerStart(parts: readonly MessagePart[]): number {
  for (let index = parts.length - 1; index >= 0; index -= 1) {
    const part = parts[index];
    if (rendersNothing(part)) continue;
    if (isTextType(part.type as string)) continue;
    return index + 1;
  }
  return 0;
}

/**
 * The live timeline: runs of consecutive steps, broken by anything else.
 *
 * This is the layout a turn has while it streams, and — because the fold stores
 * segments rather than steps — the layout it goes back to when expanded. There
 * is exactly one function producing it, so the two can never drift.
 */
function buildTimeline(
  parts: readonly MessagePart[],
  showToolActivity: boolean,
): CodingTimelineSegment[] {
  const segments: CodingTimelineSegment[] = [];
  let run: ActivityItem[] = [];
  let lastSignature: string | null = null;

  const flush = () => {
    if (run.length === 0) return;
    const stepCount = run.reduce((total, item) => total + item.duplicateCount, 0);

    // A lone step is its own row already. Wrapping it would trade a visible step
    // for a chevron that hides one thing.
    if (stepCount < 2) {
      for (const item of run) segments.push({ kind: "part", index: item.index });
    } else {
      segments.push({
        kind: "run",
        id: `coding-run-${run[0].index}`,
        items: run,
        stepCount,
      });
    }

    run = [];
    lastSignature = null;
  };

  parts.forEach((part, index) => {
    // Bookkeeping the transcript never draws — transparent to grouping, exactly
    // as it is for the assistant surface, or a run would split wherever the
    // stream happened to interleave a `data-status` frame.
    if (rendersNothing(part)) return;

    if (!joinsRun(part, showToolActivity)) {
      flush();
      segments.push({ kind: "part", index });
      return;
    }

    const signature = partSignature(part);
    if (signature === lastSignature && run.length > 0) {
      run[run.length - 1].duplicateCount += 1;
      return;
    }

    run.push({ index, duplicateCount: 1 });
    lastSignature = signature;
  });

  flush();

  return segments;
}

/** Last part index a segment covers, for splitting the timeline at the answer. */
function segmentEnd(segment: CodingTimelineSegment): number {
  if (segment.kind === "part") return segment.index;
  return segment.items[segment.items.length - 1].index;
}

/**
 * Steps a stretch of timeline represents.
 *
 * Counts work, not words: the narration between steps rides along inside the
 * fold but is not something the agent *did*, so "14 steps" stays a count of
 * commands, reads and searches.
 */
function countSteps(
  segments: readonly CodingTimelineSegment[],
  parts: readonly MessagePart[],
  showToolActivity: boolean,
): number {
  return segments.reduce((total, segment) => {
    if (segment.kind === "run") return total + segment.stepCount;
    return total + (joinsRun(parts[segment.index], showToolActivity) ? 1 : 0);
  }, 0);
}

/**
 * Lays out a coding turn.
 *
 * While it streams, the timeline as-is. Once it ends, the stretch of timeline
 * before the closing answer moves behind a single summary row — unchanged, not
 * rebuilt — and the answer keeps its place below.
 */
export function planCodingTurn(
  parts: readonly MessagePart[],
  options: CodingTurnOptions,
): CodingTurnSegment[] {
  const { isMessageStreaming, showToolActivity } = options;
  const timeline = buildTimeline(parts, showToolActivity);
  if (isMessageStreaming) return timeline;

  const foldEnd = answerStart(parts);
  const folded = timeline.filter((segment) => segmentEnd(segment) < foldEnd);
  const answer = timeline.filter((segment) => segmentEnd(segment) >= foldEnd);

  const stepCount = countSteps(folded, parts, showToolActivity);

  // Nothing worth hiding: one step, or a turn that only ever spoke.
  if (stepCount < 2) return timeline;

  const first = folded[0];
  const startIndex = first.kind === "part" ? first.index : first.items[0].index;

  return [
    { kind: "summary", id: `coding-turn-${startIndex}`, stepCount, segments: folded },
    ...answer,
  ];
}

/**
 * How long a turn took, as a person would say it.
 *
 * Seconds below a minute, minutes and seconds below an hour, hours and minutes
 * above it. Never zero-padded — this is a label, not a stopwatch.
 */
export function formatTurnDuration(ms: number): string | undefined {
  if (!Number.isFinite(ms) || ms < 0) return undefined;

  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes < 60) return seconds === 0 ? `${minutes}m` : `${minutes}m ${seconds}s`;

  const hours = Math.floor(minutes / 60);
  const restMinutes = minutes % 60;
  return restMinutes === 0 ? `${hours}h` : `${hours}h ${restMinutes}m`;
}

/** Timing and outcome a coding turn carries on its message metadata. */
export interface CodingTurnMetadata {
  /** `interrupted` means the sandbox lost its socket mid-turn and may resume. */
  status?: "streaming" | "interrupted" | "complete" | "failed";
  finishReason?: string;
  startedAt?: number;
  completedAt?: number;
}

/**
 * The turn's duration, or undefined when it cannot be known.
 *
 * Turns recorded before the gateway stamped timestamps carry neither, and a
 * summary row simply omits the duration rather than inventing one.
 */
export function turnDuration(metadata: CodingTurnMetadata | undefined): string | undefined {
  const { startedAt, completedAt } = metadata ?? {};
  if (typeof startedAt !== "number" || typeof completedAt !== "number") return undefined;
  return formatTurnDuration(completedAt - startedAt);
}

/** One file in the turn's change summary. */
export interface CodingChangedFile {
  path: string;
  status: string;
  additions: number;
  deletions: number;
}

export interface CodingChangeSummary {
  files: CodingChangedFile[];
  additions: number;
  deletions: number;
}

/**
 * Every file the turn touched, for the card under the answer.
 *
 * The sandbox already reports one `data-file-change` per path (it upserts them
 * as patches arrive), but a reload can replay a turn whose parts were written
 * across flushes, so paths are de-duplicated here too — last write wins, since
 * the authoritative line counts arrive after the first sighting of a file.
 */
export function summarizeChangedFiles(parts: readonly MessagePart[]): CodingChangeSummary {
  const byPath = new Map<string, CodingChangedFile>();

  for (const part of parts) {
    if ((part.type as string) !== "data-file-change") continue;
    const data = (part as { data?: Record<string, unknown> }).data ?? {};
    const path = typeof data.path === "string" ? data.path : "";
    if (path.length === 0) continue;

    byPath.set(path, {
      path,
      status: typeof data.status === "string" ? data.status : "modified",
      additions: typeof data.additions === "number" ? data.additions : 0,
      deletions: typeof data.deletions === "number" ? data.deletions : 0,
    });
  }

  const files = Array.from(byPath.values());
  return {
    files,
    additions: files.reduce((total, file) => total + file.additions, 0),
    deletions: files.reduce((total, file) => total + file.deletions, 0),
  };
}
